import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readFields } from "@/office/word/layout";
import { readTables } from "@/office/word/tables";

export const lektionTabellenRechnen: Lesson = {
  id: "rechnen",
  title: "Text in Tabelle umwandeln und in Tabellen rechnen",
  summary: "Aus einer Tab-Liste eine Tabelle machen – und eine Summe, die Word selbst ausrechnet.",
  durationMinutes: 9,
  learningGoals: [
    "Text mit Trennzeichen in eine Tabelle umwandeln",
    "Eine Summe mit einer Tabellenformel berechnen",
    "Wissen, wann eine Excel-Tabelle die bessere Wahl ist",
  ],
  steps: [
    {
      id: "text-in-tabelle",
      title: "Text in Tabelle umwandeln",
      explanation: [
        "Listen, die mit Tabs oder Semikolons getrennt sind – etwa aus einer E-Mail oder " +
          "einem Export –, müssen nicht abgetippt werden. Markieren, dann „Einfügen“ → " +
          "„Tabelle“ → „Text in Tabelle umwandeln“. Word erkennt das Trennzeichen meist " +
          "selbst und schlägt die Spaltenzahl vor.",
        "Der umgekehrte Weg steht unter „Layout“ → „In Text konvertieren“, wenn eine " +
          "Tabelle wieder zu einer Tab-Liste werden soll.",
      ],
      keyPoints: [
        "Einfügen → Tabelle → Text in Tabelle umwandeln.",
        "Trennzeichen: Tabstopp, Semikolon, Absatz oder ein eigenes Zeichen.",
        "Layout → In Text konvertieren für den Rückweg.",
      ],
      task: {
        instruction:
          "Schreibe drei Zeilen wie „Miete;850“, „Strom;60“, „Internet;35“ und wandle sie in " +
          "eine Tabelle mit zwei Spalten um.",
        hint: "Die drei Zeilen markieren, Einfügen → Tabelle → Text in Tabelle umwandeln, Trennzeichen „Semikolon“.",
        check: async () => {
          const tabellen = await readTables();
          const passend = tabellen.filter((tabelle) => tabelle.columnCount === 2 && tabelle.rowCount >= 3);
          const reste = tabellen.some((tabelle) => tabelle.values.flat().some((zelle) => zelle.includes(";")));
          return fromCriteria(
            [
              criterion("Eine Tabelle mit zwei Spalten und mindestens drei Zeilen", passend.length > 0),
              criterion("Keine Semikolons mehr in den Zellen", !reste),
            ],
            {
              success: "Aus der Liste ist eine Tabelle geworden – ohne ein Wort abzutippen.",
              failure: "Die Umwandlung ist noch nicht vollständig.",
              hint: "Stehen die Semikolons noch in den Zellen, war beim Umwandeln ein anderes Trennzeichen gewählt.",
            },
          );
        },
      },
    },
    {
      id: "summe",
      title: "Eine Summe in der Tabelle",
      explanation: [
        "Word kann in Tabellen einfache Rechnungen ausführen. Cursor in die leere Zelle unter " +
          "den Zahlen, dann „Layout“ → „Formel“. Word schlägt meist =SUM(ABOVE) vor – die " +
          "Summe aller Zahlen darüber. LEFT, RIGHT und BELOW funktionieren entsprechend, " +
          "ebenso AVERAGE, MIN und MAX.",
        "Die Formel ist ein Feld. Ändert sich eine Zahl, rechnet Word nicht sofort neu: " +
          "Feld markieren und F9 drücken – oder vor dem Drucken Strg + A, F9 für das ganze " +
          "Dokument.",
        "Das zeigt auch die Grenze: Sobald mehr gerechnet wird als eine Summe, gehört die " +
          "Tabelle nach Excel. Word-Tabellenformeln kennen keine Bezüge, die mitwachsen, " +
          "und keine Prüfung, ob die Zahlen stimmen.",
      ],
      keyPoints: [
        "Layout → Formel → =SUM(ABOVE).",
        "F9 aktualisiert die Formel.",
        "Mehr als eine Summe? Dann Excel.",
      ],
      task: {
        instruction: "Füge unter deinen Beträgen eine Zeile an und berechne dort die Summe mit einer Tabellenformel.",
        hint: "Cursor in die letzte Zeile, Tab am Ende fügt eine Zeile an, dann Layout → Formel → =SUM(ABOVE).",
        check: async () => {
          const felder = await readFields();
          if (felder === null) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Felder zur Verfügung (ab WordApi 1.4).",
              "Mit Alt + F9 zeigt sich die Formel als { =SUM(ABOVE) }. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          const formeln = felder.filter((feld) => feld.code.startsWith("="));
          return fromCriteria([criterion(`Tabellenformeln im Dokument: ${formeln.length}`, formeln.length > 0)], {
            success: "Die Summe rechnet Word – F9 hält sie aktuell.",
            failure: "Es gibt noch keine Tabellenformel.",
            hint: "Eine getippte Summe zählt nicht; nutze Layout → Formel.",
          });
        },
      },
    },
  ],
};
